/**
 * Recipes: list, detail, create/update, save-as-version, version restore and
 * delete, the print read model, the live-maths preview,
 * the hero-image upload and the add-line typeahead. Ports legacy
 * /recipes/* and /api/recipe/<id>/cost (docs/parity/recipes.md), built
 * against pleach/api/recipes.py, pleach/api/files.py and pleach/api/trash.py.
 *
 * Every cost figure is computed server-side (the engine is the single source
 * of truth, including its known v0.1 defects): the editor never re-derives a
 * cost, it sends the draft to POST /api/recipes/preview (debounced ~250 ms)
 * and renders what comes back.
 *
 * Query keys all start with 'recipes' so the global `costs.changed` handler in
 * lib/live.ts refreshes every cost-shaped view in this domain. List keys use
 * the API's own query-param names with falsy filters dropped, so
 * ['recipes', 'list', {}] and ['recipes', 'list', { ready_only: true }] share a
 * cache entry with the Allergens and Prep list pickers that read the same
 * endpoint.
 */
import { queryOptions, useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, rawFetch, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { registerLive } from '@/lib/live';

export type RecipeListItem = components['schemas']['RecipeListItem'];
export type RecipeListOut = components['schemas']['RecipeListOut'];
export type RecipeDetail = components['schemas']['RecipeDetailOut'];
export type RecipeLine = components['schemas']['RecipeLineOut'];
export type RecipeLineInput = components['schemas']['RecipeLineInput'];
export type RecipeCreateIn = components['schemas']['RecipeCreateIn'];
export type RecipeUpdateIn = components['schemas']['RecipeUpdateIn'];
export type RecipeVersionCreateIn = components['schemas']['RecipeVersionCreateIn'];
export type RecipePreviewIn = components['schemas']['RecipePreviewIn'];
export type RecipePreviewOut = components['schemas']['RecipePreviewOut'];
export type RecipeSnapshot = components['schemas']['RecipeSnapshotOut'];
export type RecipeConsumer = components['schemas']['RecipeConsumerOut'];
export type RecipePrintOut = components['schemas']['RecipePrintOut'];
export type AllergenRollup = components['schemas']['AllergenRollupOut'];
export type PortionMismatch = components['schemas']['PortionMismatchOut'];
export type LineSearchResult = components['schemas']['SearchResultOut'];
export type UploadOut = components['schemas']['UploadOut'];

const DOMAIN = 'recipes' as const;

export type RecipeListFilters = {
  search?: string;
  unused_only?: boolean;
  ready_only?: boolean;
};

/** Drop falsy filters so an unfiltered list is `{}` (see file header). */
function normFilters(f: RecipeListFilters): RecipeListFilters {
  const out: RecipeListFilters = {};
  if (f.search && f.search.trim()) out.search = f.search.trim();
  if (f.unused_only) out.unused_only = true;
  if (f.ready_only) out.ready_only = true;
  return out;
}

/** Print-options flags, one per legacy checkbox (_recipe_print_modal.html). */
export type PrintOpts = {
  family: boolean;
  yield: boolean;
  servings: boolean;
  serving_weight: boolean;
  photo: boolean;
  instructions: boolean;
  recipe_cost: boolean;
  pct: boolean;
  stock_cost: boolean;
  stock_unit: boolean;
  costing: boolean;
  labour: boolean;
  used_in: boolean;
  allergens: boolean;
};

export type PrintParams = { opts: PrintOpts; scale: number; version: number | null };

export const recipesKeys = {
  all: [DOMAIN] as const,
  lists: [DOMAIN, 'list'] as const,
  list: (f: RecipeListFilters) => [DOMAIN, 'list', normFilters(f)] as const,
  detail: (id: number) => [DOMAIN, 'detail', id] as const,
  snapshot: (id: number, snapId: number) => [DOMAIN, 'detail', id, 'version', snapId] as const,
  print: (id: number, p: PrintParams) => [DOMAIN, 'print', id, p] as const,
  consumers: (id: number) => [DOMAIN, 'consumers', id] as const,
};

// ── Reads ────────────────────────────────────────────────────────────────

export function recipesListQuery(filters: RecipeListFilters) {
  const f = normFilters(filters);
  return queryOptions({
    queryKey: recipesKeys.list(f),
    queryFn: () => unwrap(api.GET('/api/recipes', { params: { query: f } })),
  });
}

export function useRecipesList(filters: RecipeListFilters) {
  return useQuery({ ...recipesListQuery(filters), placeholderData: (prev) => prev });
}

/* The recipe view reads the detail (recipeDetailQuery / recipeSnapshotQuery),
   which carries every figure, flag and consumer it shows, and shares its
   cache entry with the builder; GET /api/recipes/{id}/view is not used. */

/* The recipes list's Recently deleted panel reads GET /api/trash (useTrash,
   api/queries/trash.ts): names, yields and whether each can be deleted for
   good. GET /api/recipes?include_deleted=true costs every recipe in Trash,
   which that panel never shows, so it is not used. */

export function recipeDetailQuery(id: number) {
  return queryOptions({
    queryKey: recipesKeys.detail(id),
    queryFn: () => unwrap(api.GET('/api/recipes/{recipe_id}', { params: { path: { recipe_id: id } } })),
  });
}

/** `enabled: false` holds the request (the new-recipe route reads the recipe
 *  to copy only when it has one). */
export function useRecipe(id: number, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ ...recipeDetailQuery(id), enabled });
}

/** Read-only historical overlay (`?version=<snap_id>`). Never persists. */
export function recipeSnapshotQuery(id: number, snapId: number) {
  return queryOptions({
    queryKey: recipesKeys.snapshot(id, snapId),
    queryFn: () =>
      unwrap(api.GET('/api/recipes/{recipe_id}', { params: { path: { recipe_id: id }, query: { version: snapId } } })),
  });
}

export function useRecipeSnapshot(id: number, snapId: number | null) {
  return useQuery({
    queryKey: recipesKeys.snapshot(id, snapId ?? -1),
    queryFn: () =>
      unwrap(
        api.GET('/api/recipes/{recipe_id}', { params: { path: { recipe_id: id }, query: { version: snapId! } } }),
      ),
    enabled: snapId !== null,
  });
}

/** Each saved version of a recipe, costed at today's prices (the read-only
 *  ?version= overlay; nothing is stored). Feeds the recipe view's Versions
 *  card. Shares its cache entries with the editor's version peek. */
export function useRecipeSnapshots(id: number, snapIds: number[], enabled = true) {
  return useQueries({
    queries: snapIds.map((snapId) => ({ ...recipeSnapshotQuery(id, snapId), enabled, staleTime: 60_000 })),
  });
}

export function recipePrintQuery(id: number, p: PrintParams) {
  const query: Record<string, boolean | string | number> = {};
  (Object.keys(p.opts) as Array<keyof PrintOpts>).forEach((k) => {
    if (p.opts[k]) query[k] = true;
  });
  if (p.scale && p.scale !== 1) query.scale = String(p.scale);
  if (p.version !== null) query.version = p.version;
  return queryOptions({
    queryKey: recipesKeys.print(id, p),
    queryFn: () =>
      unwrap(
        api.GET('/api/recipes/{recipe_id}/print', {
          params: { path: { recipe_id: id }, query },
        }),
      ),
  });
}

export function useRecipePrint(id: number, p: PrintParams) {
  return useQuery(recipePrintQuery(id, p));
}

/** Where a recipe is used (dish/recipe/menu/prep). Feeds the delete confirm. */
export function fetchRecipeConsumers(id: number): Promise<RecipeConsumer[]> {
  return unwrap(api.GET('/api/recipes/{recipe_id}/consumers', { params: { path: { recipe_id: id } } }));
}

/** Live maths for a draft. Never persists (SAVEPOINT + rollback server-side). */
export function previewRecipe(body: RecipePreviewIn, signal?: AbortSignal): Promise<RecipePreviewOut> {
  return unwrap(api.POST('/api/recipes/preview', { body, signal }));
}

/** Add-line typeahead: ingredients + sub-recipes (GET /api/search). The
 *  recipe being edited is excluded so it can't be added to itself. */
export function searchRecipeLines(q: string, excludeRecipe?: number | null): Promise<LineSearchResult[]> {
  const query = q.trim();
  if (!query) return Promise.resolve([]);
  return unwrap(
    api.GET('/api/search', {
      params: { query: { q: query, exclude_recipe: excludeRecipe ?? undefined } },
    }),
  );
}

/** Hero photo upload. Returns `{ key, url }`: send `key` as `image_path` on
 *  save, render `url`. The server re-compresses (~1 MB cap) and deletes the
 *  old file itself when a save replaces it. */
export async function uploadRecipeImage(file: File): Promise<UploadOut> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await rawFetch('/api/files/recipe-image', { method: 'POST', body: fd });
  return (await res.json()) as UploadOut;
}

// ── Writes ───────────────────────────────────────────────────────────────

/** Refresh everything in the domain except the detail entry a mutation has
 *  just written itself (no point refetching our own response). */
function invalidateDomain(qc: QueryClient, exceptDetailId?: number) {
  void qc.invalidateQueries({
    queryKey: recipesKeys.all,
    predicate: (q) =>
      exceptDetailId === undefined ||
      !(q.queryKey[1] === 'detail' && q.queryKey[2] === exceptDetailId && q.queryKey.length === 3),
  });
  // Cost-shaped screens in other domains read recipe figures too.
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
}

export function useCreateRecipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecipeCreateIn) => unwrap(api.POST('/api/recipes', { body })),
    onSuccess: () => invalidateDomain(qc),
  });
}

/** PUT: one aggregate save (fields + portions + photo + full line set).
 *  Throws ApiError 'conflict' on a stale `version` (screen opens ConflictDialog). */
export function useUpdateRecipe(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecipeUpdateIn) =>
      unwrap(api.PUT('/api/recipes/{recipe_id}', { params: { path: { recipe_id: id } }, body })),
    onSuccess: (data) => {
      qc.setQueryData(recipesKeys.detail(id), data);
      invalidateDomain(qc, id);
    },
  });
}

/** Plain PUT for the one case the hook can't serve: the create screen's
 *  follow-up save of portion fields on a recipe that didn't exist when the
 *  screen mounted (create accepts no portion fields, a legacy quirk the
 *  API keeps). */
export function putRecipe(id: number, body: RecipeUpdateIn): Promise<RecipeDetail> {
  return unwrap(api.PUT('/api/recipes/{recipe_id}', { params: { path: { recipe_id: id } }, body }));
}

/** "Save as new version": archives the current live state, applies the body. */
export function useSaveRecipeVersion(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecipeVersionCreateIn) =>
      unwrap(api.POST('/api/recipes/{recipe_id}/versions', { params: { path: { recipe_id: id } }, body })),
    onSuccess: (data) => {
      qc.setQueryData(recipesKeys.detail(id), data);
      invalidateDomain(qc, id);
    },
  });
}

/** Restore a snapshot into the live recipe (itself snapshotted first). */
export function useRestoreRecipeVersion(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { snapId: number; version: number }) =>
      unwrap(
        api.POST('/api/recipes/{recipe_id}/versions/{snap_id}/restore', {
          params: { path: { recipe_id: id, snap_id: vars.snapId } },
          body: { version: vars.version },
        }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(recipesKeys.detail(id), data);
      invalidateDomain(qc, id);
    },
  });
}

export function useDeleteRecipeVersion(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapId: number) =>
      unwrap(
        api.DELETE('/api/recipes/{recipe_id}/versions/{snap_id}', {
          params: { path: { recipe_id: id, snap_id: snapId } },
        }),
      ),
    onSuccess: () => invalidateDomain(qc),
  });
}

/** Soft delete (moves to Recently Deleted). No server-side consumer check:
 *  the screen asks first, using fetchRecipeConsumers. */
export function useDeleteRecipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => unwrap(api.DELETE('/api/recipes/{recipe_id}', { params: { path: { recipe_id: id } } })),
    onSuccess: () => {
      invalidateDomain(qc);
      void qc.invalidateQueries({ queryKey: ['trash'] });
    },
  });
}

/** Undo a soft delete (lives on the Trash router; no version check). */
export function useRestoreRecipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      unwrap(api.POST('/api/recipes/{recipe_id}/restore', { params: { path: { recipe_id: id } } })),
    onSuccess: () => {
      invalidateDomain(qc);
      void qc.invalidateQueries({ queryKey: ['trash'] });
    },
  });
}

/** Hard delete. 409 `has_references` while another live recipe still uses
 *  this one as a sub-recipe (a guard legacy did not have). */
export function usePurgeRecipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => unwrap(api.POST('/api/recipes/{recipe_id}/purge', { params: { path: { recipe_id: id } } })),
    onSuccess: (_r, id) => {
      qc.removeQueries({ queryKey: recipesKeys.detail(id) });
      invalidateDomain(qc);
      void qc.invalidateQueries({ queryKey: ['trash'] });
    },
  });
}

// ── Live updates ─────────────────────────────────────────────────────────
// Always invalidate, including the author's own events: `mine` is per user,
// not per tab, so skipping would leave the author's other tabs stale. The
// cost is one redundant refetch in the saving tab. Toasts about "someone
// else changed this" live in the editor, which checks isMine() itself.
registerLive('recipe.', (_evt, qc) => invalidateDomain(qc));
// An ingredient rename or unit change alters line descriptions and stock
// units on every recipe that uses it, even when no price moved (price moves
// also fire costs.changed, which lib/live.ts already maps to this domain).
// A stock count or delivery moves no cost and no name, so it is skipped:
// otherwise every stocktake in another tab refetches every recipe query.
registerLive('ingredient.', (evt, qc) => {
  if (evt.type === 'ingredient.stock_updated') return;
  void qc.invalidateQueries({ queryKey: recipesKeys.all });
});
// Labour rate / include-labour-in-COGS feed every figure on the editor.
registerLive('settings.', (_evt, qc) => void qc.invalidateQueries({ queryKey: recipesKeys.all }));
